/**
 * Assinaturas — o modo recorrente (spec 02 §5.4 · DS-30 · passo 4.3c).
 *
 * O campo que mais importa aqui é o que NÃO é gravado: `mrrCorrente`. Ele é leitura
 * da view `v_mrr_ativo` e vale `null` quando a vigência ainda não começou ou o perfil não vê valores — que é
 * diferente de zero. A DS-60 separa as duas perguntas: a Situação do pedido lê
 * `status = 'ativa'` ignorando a vigência; o MRR só conta o que já está entrando.
 */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'

export const cicloDaAssinatura = z.enum(['mensal', 'anual'])
export const statusDaAssinatura = z.enum(['ativa', 'suspensa', 'cancelada', 'encerrada'])
/** Voluntário (o cliente saiu) e involuntário (o pagamento falhou) pedem ações diferentes. */
export const tipoDeChurn = z.enum(['voluntário', 'involuntário'])

export const assinatura = z.object({
  id: uuid,
  pedidoId: uuid,
  pedidoCodigo: z.string(),
  itemPedidoId: uuid.nullable(),
  ciclo: cicloDaAssinatura,
  /** 1–28 (DS-30): dia que existe em todo mês. */
  diaVencimento: z.number().int().min(1).max(28),
  mrr: z.string().nullable(),
  moeda: codigoDeMoeda,
  vigenciaInicio: dataNominal,
  /** `null` = renovação automática, que é o caso comum. */
  vigenciaFim: dataNominal.nullable(),
  status: statusDaAssinatura,
  churnTipo: tipoDeChurn.nullable(),
  proximoCiclo: dataNominal,
  /**
   * O MRR que está ENTRANDO hoje — da view, nunca gravado.
   *
   * `null` sem permissão financeira ou quando a vigência não começou: é diferente de `'0.00'`, que descreveria
   * uma assinatura ativa sem valor. A distinção é a DS-60.
   */
  mrrCorrente: z.string().nullable(),
})
export type Assinatura = z.infer<typeof assinatura>

export const entradaDeAssinatura = z.object({
  pedidoId: uuid,
  itemPedidoId: uuid.nullish(),
  ciclo: cicloDaAssinatura,
  diaVencimento: z.number().int().min(1).max(28),
  mrr: z.string(),
  moeda: codigoDeMoeda.nullish(),
  vigenciaInicio: dataNominal,
  vigenciaFim: dataNominal.nullish(),
  proximoCiclo: dataNominal.nullish(),
})
export type EntradaDeAssinatura = z.input<typeof entradaDeAssinatura>

/** Cancelar EXIGE o tipo de churn — é a informação que não volta depois. */
export const entradaDeCancelamentoDeAssinatura = z.object({ churnTipo: tipoDeChurn })
export type EntradaDeCancelamentoDeAssinatura = z.input<typeof entradaDeCancelamentoDeAssinatura>

/** Job diário que materializa exatamente um recebível por competência de assinatura. */
export const FILA_GERAR_CICLOS_ASSINATURA = 'assinatura.gerar-ciclos'
